import { Trans, useTranslation } from "react-i18next";
import { parseHtml } from "@components/HTMLParser/utils";
import { IconButton } from "@components/IconButton/IconButton";
import { CrossIcon } from "@components/icons";
import { useHideNonProfitProvidersMutation } from "@services/nonProfits/mutations";
import type { INonProfitProvider } from "@services/nonProfits/types";
import type { IOverviewJointBillService } from "@services/overview/types";
import { formatImageSrc } from "@utils/common";
import { JointBillTableRowLayout } from "../../../../layouts/JointBillTableRowLayout/JointBillTableRowLayout";
import { NonProfitProviderForm } from "../NonProfitProviders/components/NonProfitProviderRow/components/NonProfitProviderForm/NonProfitProviderForm";

interface ISingleNonProfitProviderProps {
  nonProfitProvider: INonProfitProvider;
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  periodId: number;
}

export const SingleNonProfitProvider: React.FC<
  ISingleNonProfitProviderProps
> = ({ nonProfitProvider, jointBillServiceId, periodId }) => {
  const { t } = useTranslation();

  const { mutateAsync: hideNonProfitProviders } =
    useHideNonProfitProvidersMutation(jointBillServiceId);

  return (
    <JointBillTableRowLayout
      fullWidthBorderTop={true}
      mergeCenterCells={true}
      className="bg-[#FFE000]"
      nameCell={
        <div className="flex flex-col items-start gap-4 md:flex-row md:items-center">
          <div className="h-[55px] w-[165px] flex-shrink-0">
            <img
              className="h-full w-full object-contain"
              src={formatImageSrc(nonProfitProvider.logo)}
              alt={nonProfitProvider.logo.fileName}
            />
          </div>
          <p className="w-full text-xl font-bold leading-6">
            <Trans
              i18nKey={`nonProfitProviders.projects.${nonProfitProvider.serviceProviderId}.name`}
              components={{
                small: <span className="text-base font-medium"></span>,
              }}
            />
          </p>
        </div>
      }
      totalCell={
        <div className="flex h-full items-center">
          <IconButton
            icon={CrossIcon}
            onClick={() => hideNonProfitProviders()}
            aria-label={t("action.close")}
          />
        </div>
      }
      detailsOpenButtonTitle={t("nonProfitProviders.actions.detailsOpenButton")}
      detailsCloseButtonTitle={t(
        "nonProfitProviders.actions.detailsCloseButton",
      )}
      billDetails={
        <div
          key={nonProfitProvider.serviceProviderId}
          className="flex flex-col items-center justify-between gap-4 py-4 first:pt-0 last:pb-0 md:flex-row"
        >
          <div className="flex flex-col items-center gap-4 md:flex-row">
            <p className="w-full pr-10 font-medium">
              {parseHtml(
                t(
                  `nonProfitProviders.projects.${nonProfitProvider.serviceProviderId}.description`,
                ),
              )}
            </p>
          </div>
          <NonProfitProviderForm
            jointBillServiceId={jointBillServiceId}
            provider={nonProfitProvider}
            periodId={periodId}
          />
        </div>
      }
    />
  );
};
